import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '../catalyst/button';
import { Dialog, DialogActions, DialogTitle, DialogDescription } from '../catalyst/dialog';
import SpinnerIcon from '../icons/Spinner';

/**
 * @typedef {Object} ConfirmDialogProps
 * @property {boolean} isOpen - Whether the dialog is open.
 * @property {function} setIsOpen - Function to set the dialog open state.
 * @property {string} title - The title of the dialog.
 * @property {string} message - The message of the dialog.
 * @property {function} onConfirm - Function to call when the confirm button is clicked.
 * @property {function} onCancel - Function to call when the cancel button is clicked.
 * @property {string} [confirmText='Confirm'] - The text for the confirm button.
 * @property {string} [cancelText='Cancel'] - The text for the cancel button.
 */

/**
 * ConfirmDialog component to display a confirmation dialog.
 *
 * @param {ConfirmDialogProps} props - The properties for the ConfirmDialog component.
 * @returns {JSX.Element} The rendered confirm dialog component.
 */
export default function ConfirmDialog({
  isOpen,
  setIsOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmTextProgress = 'Confirming...',
  isPending = false,
}) {
  const closeDialog = () => {
    setIsOpen(false);
    onCancel();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={setIsOpen}
    >
      <div className="sm:flex sm:items-start">
        <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
          <ExclamationTriangleIcon
            aria-hidden="true"
            className="size-6 text-red-600"
          />
        </div>
        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </div>
      </div>

      <DialogActions>
        <Button
          plain
          onClick={closeDialog}
          disabled={isPending}
        >
          {cancelText}
        </Button>
        <Button
          color="red"
          onClick={onConfirm}
          disabled={isPending}
        >
          {isPending && <SpinnerIcon className="animate-spin w-5 h-5" />}
          {isPending ? confirmTextProgress : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
